<template>
  <div class="festival-ops">
    <festival-ops-nav active-path="/festival/orders" />
    <section class="festival-ops__panel">
      <div class="festival-ops__head">
        <div>
          <h2 class="festival-ops__title">节日自营订单履约</h2>
          <p class="festival-ops__muted">订单、收货信息和物流均由节日专区独立管理。</p>
        </div>
      </div>
      <div class="festival-ops__filter">
        <el-input v-model="filter.orderId" clearable placeholder="订单号" style="width:190px" @keyup.enter.native="search" />
        <el-input v-model="filter.personName" clearable placeholder="收货人" style="width:130px" @keyup.enter.native="search" />
        <el-input v-model="filter.personTel" clearable placeholder="联系电话" style="width:150px" @keyup.enter.native="search" />
        <el-select v-model="filter.state" clearable placeholder="全部状态" style="width:130px">
          <el-option v-for="item in states" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <el-button icon="el-icon-search" @click="search">查询</el-button>
        <el-button icon="el-icon-refresh" @click="reset">重置</el-button>
      </div>
    </section>

    <section class="festival-ops__panel">
      <el-table v-loading="loading" :data="rows" border>
        <el-table-column prop="orderId" label="订单号" width="190" />
        <el-table-column label="商品" min-width="210">
          <template slot-scope="scope">
            <div class="product-cell">
              <el-image :src="imageUrl(scope.row.imageUrl)" fit="cover" class="product-cell__image" />
              <div><strong>{{ scope.row.productName }}</strong><small>¥{{ scope.row.unitPrice }} × {{ scope.row.quantity }} {{ scope.row.unit }}</small></div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="实付金额" width="100" align="center">
          <template slot-scope="scope"><span class="festival-ops__price">¥{{ scope.row.totalAmount }}</span></template>
        </el-table-column>
        <el-table-column label="收货信息" min-width="230">
          <template slot-scope="scope">
            <div>{{ scope.row.personName }} · {{ scope.row.personTel }}</div>
            <small class="muted-line">{{ scope.row.address }}</small>
          </template>
        </el-table-column>
        <el-table-column label="物流" min-width="170">
          <template slot-scope="scope">
            <div v-if="scope.row.trackingNo">{{ scope.row.deliveryCompany }}</div>
            <small v-if="scope.row.trackingNo" class="muted-line">{{ scope.row.trackingNo }}</small>
            <template v-else-if="scope.row.shipDeadline">
              <small :class="isShipOverdue(scope.row) ? 'overdue-line' : 'muted-line'">预计 {{ scope.row.shipDeadline }} 前发货</small>
              <el-tag v-if="isShipOverdue(scope.row)" type="danger" size="mini">已超期</el-tag>
            </template>
            <span v-else class="muted-line">—</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" min-width="150" align="center">
          <template slot-scope="scope">
            <el-tag :type="stateTag(scope.row.state)" size="small">{{ stateName(scope.row.state) }}</el-tag>
            <small v-if="scope.row.refundReason" class="muted-line refund-line">退款原因：{{ scope.row.refundReason }}</small>
            <small v-if="scope.row.refundRemark" class="muted-line refund-line">{{ scope.row.state === 'E' ? '失败原因' : '处理说明' }}：{{ scope.row.refundRemark }}</small>
            <small v-if="scope.row.state === 'X' && scope.row.cancelReason" class="muted-line refund-line">{{ scope.row.cancelReason }}</small>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="下单时间" width="165" />
        <el-table-column label="操作" width="170" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button v-if="scope.row.state === 'N'" type="text" icon="el-icon-s-claim" @click="openAccept(scope.row)">接单</el-button>
            <el-button v-if="scope.row.state === 'S'" type="text" icon="el-icon-truck" @click="openShip(scope.row)">发货</el-button>
            <el-button v-if="scope.row.state === 'D'" type="text" icon="el-icon-circle-check" @click="complete(scope.row)">完成</el-button>
            <el-button v-if="scope.row.state === 'A'" type="text" icon="el-icon-check" @click="approveRefund(scope.row)">同意退款</el-button>
            <el-button v-if="scope.row.state === 'A'" type="text" icon="el-icon-close" @click="rejectRefund(scope.row)">驳回</el-button>
            <el-button v-if="scope.row.state === 'E'" type="text" icon="el-icon-refresh-right" @click="approveRefund(scope.row)">重试退款</el-button>
            <span v-if="!['N', 'S', 'D', 'A', 'E'].includes(scope.row.state)" class="muted-line">—</span>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination class="festival-ops__pagination" :current-page.sync="page.current" :page-size.sync="page.size"
        :page-sizes="[10, 20, 50]" :total="page.total" layout="total, sizes, prev, pager, next"
        @size-change="load" @current-change="load" />
    </section>

    <el-dialog title="接单" :visible.sync="acceptDialog" width="420px">
      <el-form label-position="top">
        <el-form-item label="预计发货时间" required>
          <el-date-picker v-model="acceptForm.shipDeadline" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
            :picker-options="{ disabledDate: date => date.getTime() < Date.now() - 86400000 }"
            placeholder="请选择预计发货时间" style="width:100%" />
        </el-form-item>
        <p class="muted-line">接单后用户申请退款需平台审核；预计发货时间将展示给用户。</p>
      </el-form>
      <span slot="footer">
        <el-button @click="acceptDialog = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="accept">确认接单</el-button>
      </span>
    </el-dialog>

    <el-dialog title="填写发货信息" :visible.sync="shipDialog" width="480px">
      <el-form ref="shipForm" :model="shipForm" :rules="shipRules" label-position="top">
        <el-form-item label="物流公司" prop="deliveryCompany"><el-input v-model="shipForm.deliveryCompany" maxlength="64" placeholder="如顺丰速运" /></el-form-item>
        <el-form-item label="物流单号" prop="trackingNo"><el-input v-model="shipForm.trackingNo" maxlength="128" placeholder="请输入物流单号" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="shipDialog = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="ship">确认发货</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { auditFestivalRefund, fulfillFestivalOrder, listFestivalOrders } from '@/api/life/festivalApi'
import { resolveImageUrl } from '@/utils/image-url'
import { dateFormat, timeFormat } from '@/utils/dateUtil'
import FestivalOpsNav from './festivalNav.vue'
import './festival-ops.scss'

export default {
  name: 'FestivalOrders',
  components: { FestivalOpsNav },
  data() {
    return {
      loading: false,
      saving: false,
      shipDialog: false,
      acceptDialog: false,
      acceptForm: { orderId: '', shipDeadline: '' },
      rows: [],
      states: [
        { label: '待支付', value: 'W' }, { label: '待接单', value: 'N' }, { label: '待发货', value: 'S' },
        { label: '已发货', value: 'D' }, { label: '已完成', value: 'C' },
        { label: '已取消', value: 'X' }, { label: '支付失败', value: 'F' },
        { label: '退款审核中', value: 'A' }, { label: '退款处理中', value: 'P' },
        { label: '已退款', value: 'R' }, { label: '退款失败', value: 'E' }
      ],
      filter: { orderId: '', personName: '', personTel: '', state: '' },
      page: { current: 1, size: 20, total: 0 },
      shipForm: { orderId: '', deliveryCompany: '', trackingNo: '' },
      shipRules: {
        deliveryCompany: [{ required: true, message: '请输入物流公司', trigger: 'blur' }],
        trackingNo: [{ required: true, message: '请输入物流单号', trigger: 'blur' }]
      }
    }
  },
  created() { this.load() },
  methods: {
    imageUrl: resolveImageUrl,
    stateName(state) { const item = this.states.find(entry => entry.value === state); return item ? item.label : state },
    isShipOverdue(row) {
      if (row.state !== 'S' || !row.shipDeadline) return false
      return new Date(String(row.shipDeadline).replace(/-/g, '/')).getTime() <= Date.now()
    },
    openAccept(row) {
      const deadline = new Date(Date.now() + 48 * 3600 * 1000)
      this.acceptForm = { orderId: row.orderId, shipDeadline: `${dateFormat(deadline)} ${timeFormat(deadline)}` }
      this.acceptDialog = true
    },
    async accept() {
      if (!this.acceptForm.shipDeadline) {
        this.$message.warning('请选择预计发货时间')
        return
      }
      this.saving = true
      try {
        await fulfillFestivalOrder({ ...this.acceptForm, action: 'ACCEPT' })
        this.$message.success('已接单')
        this.acceptDialog = false
        await this.load()
      } catch (error) {
        this.$message.error(error.message || '接单失败')
      } finally { this.saving = false }
    },
    stateTag(state) { return { W: 'info', N: 'warning', S: 'warning', D: '', C: 'success', X: 'danger', F: 'danger', A: 'warning', P: 'warning', R: 'info', E: 'danger' }[state] || 'info' },
    search() { this.page.current = 1; this.load() },
    reset() { this.filter = { orderId: '', personName: '', personTel: '', state: '' }; this.search() },
    async load() {
      this.loading = true
      try {
        const res = await listFestivalOrders({ ...this.filter, page: this.page.current, row: this.page.size })
        this.rows = res.data || []
        this.page.total = Number(res.total || 0)
      } catch (error) {
        this.$message.error(error.message || '加载节日订单失败')
      } finally { this.loading = false }
    },
    openShip(row) {
      this.shipForm = { orderId: row.orderId, deliveryCompany: '', trackingNo: '' }
      this.shipDialog = true
      this.$nextTick(() => this.$refs.shipForm && this.$refs.shipForm.clearValidate())
    },
    ship() {
      this.$refs.shipForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          await fulfillFestivalOrder({ ...this.shipForm, action: 'SHIP' })
          this.$message.success('订单已发货')
          this.shipDialog = false
          await this.load()
        } catch (error) {
          this.$message.error(error.message || '发货失败')
        } finally { this.saving = false }
      })
    },
    complete(row) {
      this.$confirm(`确认订单“${row.orderId}”已完成？`, '完成订单', { type: 'warning' }).then(async () => {
        try {
          await fulfillFestivalOrder({ orderId: row.orderId, action: 'COMPLETE' })
          this.$message.success('订单已完成')
          this.load()
        } catch (error) { this.$message.error(error.message || '操作失败') }
      }).catch(() => {})
    },
    approveRefund(row) {
      this.$confirm(`确认向用户原路退回订单“${row.orderId}”的全部款项？退款成功后库存将自动回补。`, '同意退款', { type: 'warning' }).then(async () => {
        const loading = this.$loading({ lock: true, text: '退款处理中...' })
        try {
          await auditFestivalRefund({ orderId: row.orderId, action: 'APPROVE' })
          this.$message.success('退款成功')
        } catch (error) {
          this.$message.error(error.message || '退款失败')
        } finally {
          loading.close()
          this.load()
        }
      }).catch(() => {})
    },
    rejectRefund(row) {
      this.$prompt('驳回后订单恢复为待发货，驳回原因将展示给用户', '驳回退款', {
        inputPlaceholder: '请输入驳回原因',
        inputValidator: value => (value && value.trim() ? true : '请输入驳回原因')
      }).then(async ({ value }) => {
        try {
          await auditFestivalRefund({ orderId: row.orderId, action: 'REJECT', remark: value.trim() })
          this.$message.success('已驳回退款申请')
          this.load()
        } catch (error) { this.$message.error(error.message || '操作失败') }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.product-cell { display:flex;align-items:center;gap:10px; }
.product-cell__image { width:46px;height:46px;flex:none;border-radius:7px; }
.product-cell div { min-width:0;display:flex;flex-direction:column; }
.product-cell strong { overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
.product-cell small,.muted-line { color:#8b94a3;font-size:12px; }
.overdue-line { display:block;color:#f56c6c;font-size:12px; }
.refund-line { display:block;margin-top:4px;line-height:1.4;text-align:left; }
</style>
